// owned-products — small pure helpers for the Products section (My Account C1,
// task T7a, design.md AC5): terminal subscription errors and the product
// search match (cancellation reversibility moved server-side, Task 13).
//
// ⚠️ AMENDED 2026-10-27 (TD-671, Task 9). ORIGINAL: this module also derived
// the owned-product set in the browser (a seed list from order lines plus a
// per-product subscription pick) from the account's raw order/subscription
// fetches. CURRENT: ownership is computed by the server and read from
// GET /me/owned-products (owned-products-api.ts), so that derivation is
// deleted and only the helpers above remain. The price-source ruling below is
// kept because it still explains why the subscription row, not an order line,
// is the renewal-amount authority.
//
// FRAMED-CORRECTION (project rule §31 — grep the actual data model before
// trusting a spec claim): design.md AC5 says renewal price should come "from
// the line's frozen recurring_amount + recurring_interval". Grepped
// internal/marketplace/model/order.go: OrderLine has NO RecurringAmount
// field — only RecurringInterval is frozen there (confirmed against the
// wire's orderLineResp in customer_handler.go too). The order line's
// unit_price is NOT a reliable stand-in either: for the ORIGIN checkout
// order, order_service.go's TD-141 comment states the recurring amount is
// deliberately skipped from that line's pricing (billed separately via
// subscription-cycle orders), so unit_price on that line is not the renewal
// price. The only reliable source for a renewal AMOUNT is therefore the
// live Subscription row (model.Subscription.RecurringAmount), which the
// design's own "and/or the live subscription row where one exists" clause
// permits — this module treats the subscription row as authoritative for
// PRICE. ⚠️ AMENDED 2026-10-04 (account-area redesign, Task 7): the original
// wording continued "and falls back to the order line's frozen
// recurring_interval (interval only, no price) when no subscription row is
// found" — that fallback RENDERED the retired "Renews <interval>" line. The
// redesign's tables show an amount only from a real subscription row and
// nothing at all otherwise, so the interval fallback is no longer a display
// contract.

// deriveTypeFacet (the type-filter facet) is RETIRED by the account-area
// redesign Task 7 (Amendment 2026-10-02 F16): the Products section's tabs
// partition rows on SUBSCRIPTION PRESENCE (the owned item's kind), not
// product type, so the facet derivation has no remaining consumer. The
// partition rule itself lives in SubscriptionsTable/OwnedProductsTable's
// shared input (ProductsTab derives it); do not resurrect a type filter
// without re-deriving the F16 ruling.

// isReversibleCancellation is RETIRED (TD-671 Task 13, R9/F14): whether a
// scheduled cancellation can still be resumed is the SERVER's verdict, carried
// as a `resume` entry in the owned item's `actions` (the rule stays the SQL
// guard `cancel_at > (now() AT TIME ZONE 'UTC')::date`, TD-462 D7: resuming
// after the last authorized collection would serve a period never billed).
// OverviewPanel keeps its own local copy (F14) — do not re-export one here.

/**
 * True when a cancel/resume failure code means the row on screen is
 * permanently stale — the janitor terminated it, or it no longer exists. Both
 * are TERMINAL: the same click can never succeed, so leaving the card as it is
 * offers a control whose only possible outcome is the identical error. Refetch
 * instead and let the server's truth replace the stale row.
 *
 * VERSION_MISMATCH is deliberately NOT here. It is retryable against a fresh
 * version, and the mutation paths already re-seed from their responses (§59).
 *
 * ⚠️ MOVED verbatim from ProductsTab.tsx by the account-area redesign Task 7
 * (same reason as isReversibleCancellation above).
 */
export function isTerminalSubscriptionError(code: string): boolean {
  return code === "CANCELLATION_ELAPSED" || code === "NOT_FOUND";
}

/**
 * True when `product` matches a case-insensitive substring search against
 * name/description. A product that failed catalog enrichment (undefined)
 * never matches a non-empty query — there is no name to search, and showing
 * it unconditionally under an active search would be confusing (project
 * rule: never fabricate/guess).
 */
export function matchesProductSearch(
  // Structural, so both the catalog Product and the owned-products wire's
  // product ref (name only: TD-671 Task 8 dropped the catalog join) qualify.
  product: { name: string; description?: string | null } | undefined,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (!product) return false;
  return (
    product.name.toLowerCase().includes(q) ||
    (product.description ?? "").toLowerCase().includes(q)
  );
}
